/*
*   File : invest.js
*   Author : https://evoqins.com
*   Description : LumpSum or SIP investment page
*   Integrations : NA
*   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";


// Components
import { PrimaryButton } from "../../Component/Buttons";
import { ProfileCard } from "../../Component/Cards";
import { CustomAmountInput } from "../../Component/FormElements";
import { Breadcrumbs, Chip, Loader } from "../../Component/Others";
import { Icon } from "../../Component/Icon";
import { OrderStatusModal } from "../../Component/Modal";
import { _checkBrokerStatus, _createAndUpdatePortFolio, _executeBrokerOrder, _formatSIPDate, _formattedAmount, _generateProductCode, _openBrokerOrderPlatform, _scrollToTop, _validateBrokerOrder } from "../../Helper";
import { APIService } from "../../Service";

//Style
import style from "../../Styles/Component/basket.module.scss";

// Custom data
const BREADCRUMBS = [
    {
        id: 1,
        label: "Explore",
        path: "/explore",
        data: {
            category_tab: 1,
        },
    },
    {
        id: 2,
        label: "Products",
        path: "/explore",
        data: {
            category_tab: 1,
        },
    },
];

const SIP_AMOUNT = [
    {
        id: 1,
        amount: 1000
    },
    {
        id: 2,
        amount: 2000
    },
    {
        id: 3,
        amount: 3000
    },
];


function Invest() {

    const navigate = useNavigate();
    const { state } = useLocation();
    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const orderStatusData = useRef();
    const amountInfoError = useRef({});

    let broker_data = JSON.parse(localStorage.getItem("indvestment_connected_broker"));
    let is_sip = state && state.investmentType === "SIP" ? true : false;
    let is_sip_due = state && state.is_sip_due;
    let basket_data = state ? state.basket_data : "";

    const [sipAmount, setSipAmount] = useState(state && state.sip ? state.sip : "");
    const [sipAmountError, setSipAmountError] = useState("");
    const [lumpSumAmount, setLumpSumAmount] = useState(state && state.lumpsum ? state.lumpsum : "");
    const [lumpSumAmountError, setLumpSumAmountError] = useState("");
    const [showInvestDetail, setShowInvestDetail] = useState(false);
    const [breadcrumbs, setBreadCrumbs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [holdingsData, setHoldingsData] = useState(state && state.holdings_data || []);
    const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
    const [pageLoader, setPageLoader] = useState(true);
    const [brokerBalance, setBrokerBalance] = useState(broker_data ? broker_data.available_cash : 0);

    useEffect(() => {

        // Scroll the page to top
        _scrollToTop();

        let sip_label = basket_data.executed ? "SIP Due" : "Start SIP";
        let lumpsum_label = state.operation == "NEW_PURCHASE" ? "Start Lumpsum" : " Invest More";

        setBreadCrumbs([
            ...BREADCRUMBS,
            {
                id: 3,
                label: "alphaBasket",
                path: "/explore",
                data: {
                    category_tab: basket_data.strategy_type === "alpha-basket-pro" ? 2 : 1,
                },
            },
            {
                id: 4,
                label: basket_data.strategy_name,
                path: "/explore/strategy-detail",
                data: {
                    type: 2,
                    strategy_id: Number(basket_data.strategy_id)
                },
            },
            {
                id: 5,
                label: state && state.investmentType == "SIP" ? sip_label : lumpsum_label,
                path: null,
                data: null
            },
        ]);

        if (state === null) {
            navigate("/explore");
        } else {
            setPageLoader(false);
        }

    }, []);

    useEffect(() => {
        if (showOrderStatusModal) {
            const order_status_modal = new Modal(document.getElementById('order-status-modal'));
            order_status_modal.show();
        }
    }, [showOrderStatusModal]);


    // handle select sip amount 
    const _handleSelectAmount = (type, selected) => {
        if (type === 1) {
            setSipAmount(selected.amount);
            setSipAmountError("");
        } else {
            setLumpSumAmount(selected.amount);
            setLumpSumAmountError("");
        }
    };

    // handle SIP amount change
    const _handleSIPAmountChange = (value) => {
        setSipAmount(value);
        setSipAmountError("");
    };

    // handle SIP amount change
    const _handleLumpSumChange = (value) => {
        setLumpSumAmount(value);
        setLumpSumAmountError("");
    };

    // handle back
    const _handleBack = () => {
        setShowInvestDetail(false);
    };


    // handle navigate - breadcrumbs
    const _handleNavigate = (data) => {
        if (data.path) {
            navigate(data.path, {
                state: {
                    "category_tab": data.data.category_tab,
                    "strategy_id": data.data.strategy_id,
                    "tenant_id": basket_data.creator_info.creator_id
                }
            });
        }
    };

    // handle Generate Portfolio
    const _handleGeneratePortfolio = () => {

        let is_valid = true;

        if (is_sip) {
            if (sipAmount.length === 0) {
                setSipAmountError("Enter SIP amount");
                is_valid = false;
            }

            // if (selectedDate === null) {
            //     setSipDateError("Select day of sip");
            //     is_valid = false;
            // }
        } else {

            if (lumpSumAmount.length === 0) {
                setLumpSumAmountError("Enter lumpsum amount");
                is_valid = false;
            } else if (lumpSumAmount < basket_data.min_investment) {
                setLumpSumAmountError(`Min investment amount is ₹${basket_data.min_investment.toLocaleString("en-IN")}`);
                is_valid = false;
            }
        }

        if (is_valid === true) {

            let invest_amount = is_sip ? sipAmount : lumpSumAmount

            // Check broker account balance
            if (invest_amount > Number(brokerBalance)) {
                toast.dismiss();
                toast.error("Insufficient Funds. Please add funds to your broker wallet to complete the transaction.");
            } else {
                setLoading(true);
                if (state.investmentType === "SIP") {
                    _generateNewSIPOrder();
                } else {
                    _generateLumpsumPortfolio();
                }


            }
        }
    };

    // handle invest 
    const _handleInvest = () => {
        setLoading(true);
        // API call
        _callGenerateProductCode();
    };

    // handle close order status modal
    const _handleCloseOrderStatusModal = () => {
        setShowOrderStatusModal(false);
        navigate(-1, { replace: true });
    };

    // handle navigate to portfolio page
    const _handleNavigatePortfolio = () => {
        navigate("/portfolio");
        setShowOrderStatusModal(false);
    };

    // handle navigate to order detail page
    const _handleNavigateOrderDetail = (portfolio_id) => {
        navigate("/orders/detail", {
            state: {
                portfolio_id,
                tenant_id: Number(basket_data.creator_info.creator_id),
            }
        });
        setShowOrderStatusModal(false);
    };

    // handle refresh broker fund info
    const _handleUpdateBrokerACBalance = () => {

        // Getting broker fund info buy calling this API
        // API: broker/fund - details
        let default_broker = JSON.parse(localStorage.getItem("indvestment_connected_broker"));
        _checkBrokerStatus(default_broker, USER_DATA.unique_identifier).then(response => {
            if (response.status_code === 200) {
                setBrokerBalance(response.data.available_cash);
                toast.dismiss();
                toast.success("Wallet Balance updated successfully");
            } else {
                toast.dismiss();
                toast.error("Something went wrong, please try again");
            }
        })

    };


    // ===================================== //
    // ============ API CALLS ============== //

    // Create new SIP - SIP Due
    const _generateNewSIPOrder = async () => {

        let date_now = new Date();
        let url = "broker/sip";
        let data = {
            "sip_amt": Number(sipAmount),
            "remaining_amt": 0,
            "strategy_type": basket_data.strategy_type || "alphaBasket",
            "strategy_id": Number(basket_data.strategy_id),
            "date": `${date_now.getFullYear()}-${date_now.getMonth() + 1}-${date_now.getDate()}`, // "2024-10-22",
            "user_id": USER_DATA.unique_identifier,
            "action": "SIP",
            "request_date": date_now,
            "portfolio_details": holdingsData.map((holding) => ({
                "isin": holding.isin,
                "alphaniti_key": holding.alphaniti_key,
                "qty": holding.qty || holding.quantity,
                "invested_price": holding.invested_amount
            })),
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {

            let updated_holdings = response.data.portfolio_details.map((sip_holding) => {
                let matching_holding = holdingsData.find(holding => holding.alphaniti_key === sip_holding.alphaniti_key);
                if (matching_holding && sip_holding.action_qty) {
                    return {
                        ...matching_holding,
                        ...sip_holding,
                        qty: sip_holding.action_qty,
                        price: sip_holding.close,
                        value: sip_holding.action_qty * sip_holding.close
                    }
                } else {
                    return null
                }
            }).filter(Boolean);

            setHoldingsData(updated_holdings);
            setShowInvestDetail(true);
            _scrollToTop();
        } else {
            toast.dismiss();
            toast.error(response.message);
        }

        setLoading(false);
    };

    // Generate portfolio - Invest
    const _generateLumpsumPortfolio = async () => {
        let url = "broker/portfolio-generation";
        let data = {
            "strategy_id": Number(basket_data.strategy_id),
            "tenant_id": Number(basket_data.creator_info.creator_id),
            "user_id": USER_DATA.unique_identifier,
            "investment_amount": Number(state.investmentType === "SIP" ? sipAmount : lumpSumAmount),
            "buy_slippage": 1,
            "action": "Portfolio Generation",
            "request_date": new Date().toLocaleString("en-US", { month: 'short', day: 'numeric', year: 'numeric' }).replace(",", ""),
            "holdings": state.holdings_data
        };

        const response = await APIService(url, data);
        if (response.status_code === 200) {
            if (response.data.add_more !== 0 || response.data.reduce !== 0) {
                amountInfoError.current = {
                    add_more: response.data.add_more,
                    reduce: response.data.reduce,
                    remaining_amount: response.data.remaining_amount
                }
            } else {
                setHoldingsData(response.data.holdings);
                setShowInvestDetail(true);
                _scrollToTop();
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
        }

        setLoading(false);
    };

    // handle call get Product code
    const _callGenerateProductCode = async () => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdingsData,
            action: "BUY",
            portfolio_id: state.portfolio_id,
            user_id: USER_DATA.unique_identifier,
            operation: state.operation
        };

        const response = await _generateProductCode(params);

        if (response.status_code === 200) {
            if (state.operation !== "NEW_PURCHASE") {
                if (broker_data.publisher) {
                    _callOpenBrokerOrderPlatform(response.data.product_code, state.portfolio_id, response.data.portfolio_action_id);
                } else {
                    // Call execute API - Manual
                    _callExecuteBrokerOrder(response.data.product_code, state.portfolio_id, response.data.portfolio_action_id);
                }
            } else {
                _savePortfolio(Number(response.data.product_code));
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoading(false);
        }

    };

    // Save portfolio details
    const _savePortfolio = async (product_code) => {
        let url = "portfolio/save";
        let holdings = holdingsData.map(holding => ({
            "action": holding.action,
            "alphaniti_key": holding.alphaniti_key,
            "isin": holding.isin,
            "quantity": Number(holding.qty) || holding.quantity,
            "exchange": holding.exchange_to_trade,
            "current_price": Number(holding.price),
            "limit_price": Number(holding.price),
            "order_type": holding.order_type,
            "order_validity": holding.order_validity,
            "product_type": holding.product_type,
            "portfolio_action_id": null,
            "portfolio_id": null,
            "portfolio_order_id": null,
            "portfolio_stock_id": null,
            "trade_id": "",
            "trade_symbol": holding.exchange_to_trade === "NSE" ? holding.nse_exchange_symbol : holding.bse_exchange_symbol,
            "trade_token": holding.exchange_to_trade === "NSE" ? Number(holding.nse_exchange_token) : Number(holding.bse_exchange_token),
        }));
        let data = {
            "client_code": broker_data.client_code,
            "execution_partner": broker_data.execution_partner,
            "investment_amount": state.investmentType === "SIP" ? Number(sipAmount) : Number(lumpSumAmount),
            "portfolio_type": "Basket",
            "product_code": product_code,
            "source": "WEB",
            "strategy_id": Number(basket_data.strategy_id),
            "tenant_id": Number(basket_data.creator_info.creator_id),
            "user_risk_id": Number(basket_data.risk_category_info.risk_category_id),
            "user_id": USER_DATA.unique_identifier,
            "holdings": holdings
        };

        let response = await APIService(url, data)
        if (response.status_code === 200) {
            // Check publisher flag and move to investment
            if (broker_data.publisher) {
                _callOpenBrokerOrderPlatform(response.data.product_code, response.data.portfolio_id, response.data.portfolio_action_id);
            } else {
                // Call execute API
                _callExecuteBrokerOrder(response.data.product_code, response.data.portfolio_id, response.data.portfolio_action_id);
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoading(false);
        }

    };

    // handle call open broker order platform
    const _callOpenBrokerOrderPlatform = async (product_code, portfolio_id, portfolio_action_id) => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdingsData,
            transaction_type: "BUY",
            portfolio_id: portfolio_id,
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _openBrokerOrderPlatform(params);

        if (response.status_code === 200) {
            // Open broker order window
            const broker_window_url = URL.createObjectURL(
                new Blob([response.result], { type: "text/html" })
            );
            // Broker order creation window
            const broker_order_window = window.open(broker_window_url, "_blank");
            let current_url = "";
            let interval = setInterval(() => {
                try {
                    current_url = broker_order_window.location.href;
                    let url_data = current_url.split('?');
                    if (url_data.length !== 1) {
                        // Taking the type and action from the URL
                        let params = new URLSearchParams(url_data[1]);
                        let access_token = params.get("request_token");
                        const type = params.get("type");
                        const action = params.get("action");
                        const status = params.get("status");
                        if (type === "basket" && action === "basket" && access_token && status === "success") {
                            clearInterval(interval);
                            // API call
                            _callValidateBrokerOrder(access_token, product_code, portfolio_id, portfolio_action_id);
                            broker_order_window.close();
                        } else if (status === "cancelled") {
                            clearInterval(interval);
                            broker_order_window.close();
                            toast.dismiss();
                            toast.error("Order has been cancelled");
                            setLoading(false);
                        }
                    }
                } catch (error) {
                    console.log("Cross-origin access error. Waiting for the window to navigate...");
                }

                if (broker_order_window.closed) {
                    clearInterval(interval);
                    if (current_url.length === 0) {
                        setLoading(false);
                    }
                };

            }, 1000);

        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoading(false);
        }

    };

    // handle call execute Broker Order 
    const _callExecuteBrokerOrder = async (product_code, portfolio_id, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: holdingsData,
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _executeBrokerOrder(params);
        if (response.status_code === 200) {
            // Create or update portfolio
            _callUpdatePortFolio(product_code, portfolio_id, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoading(false);
        }

    };

    // handle call validate broker order
    const _callValidateBrokerOrder = async (access_token, product_code, portfolio_id, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            creator_id: basket_data.creator_info.creator_id,
            access_token: access_token,
        };

        const response = await _validateBrokerOrder(params);

        if (response.status_code === 200) {
            // Create or update portfolio
            _callUpdatePortFolio(product_code, portfolio_id, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoading(false);
        }
    };

    // handle call update portfolio
    const _callUpdatePortFolio = async (product_code, portfolio_id, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            product_code: product_code,
            portfolio_id: portfolio_id,
            investment_amount: state.investmentType === "SIP" ? Number(sipAmount) : Number(lumpSumAmount),
            operation: state.operation,
            portfolio_action_id: portfolio_action_id
        };

        const response = await _createAndUpdatePortFolio(params);

        if (response.status_code === 200 || response.status_code === 202 || response.status_code === 204) {
            // Order status  = success, partial, awaiting confirmation 
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 1,
                status_message: response.message,
                portfolio_id: response.data.portfolio_id
            };
        } else if (response.status_code === 205) {
            // Order rejected
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                // portfolio_id: response.data.result.portfolio_id
            };
        } else if (response.status_code === 400) {
            // Order awaiting confirmation
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                status_code: response.status_code
            };
        } else {
            // Something went wrong
            setShowOrderStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                status_code: response.status_code
            };
        }
        setLoading(false);
    };

    return (
        <>

            {
                pageLoader === false ?
                    <section className={`${style.e_basket_detail_wrapper} pb-5`}>
                        {/* Bread crumbs */}
                        <Breadcrumbs data={breadcrumbs}
                            className="padding-14px-tb px-sm-2"
                            handleNavigate={_handleNavigate} />
                        {/* Top banner */}
                        <div className={`${style.e_top_section} border-radius-16 p-3`}>
                            <div className='row flex-wrap flex-column flex-lg-row row-gap-3'>
                                <div className='col-auto'>
                                    <div className="d-flex gap-2">
                                        <img src={basket_data.strategy_icon}
                                            draggable={false}
                                            className='w-64px w-36px-res w-48px-res bg-white p-1 border-radius-8'
                                            alt='product' />
                                        <div>
                                            <h6 className='e-font-16-res mb-1 mb-sm-2 e-alt-font-lato e-text-white e-font-18 e-font-weight-600 line-height-28px'>
                                                {basket_data.strategy_name}
                                            </h6>
                                            <div className={`${style.e_category_chip} e-padding-4px-tb-res d-flex align-items-center padding-6px-tb px-2 gap-2 e-bg-white border-radius-24 w-max-content`}>
                                                <img src={require("../../Assets/Images/Card/basket-icon.svg").default}
                                                    draggable={false}
                                                    className='w-16px-res w-20px '
                                                    alt='tag' />
                                                <span className='e-font-12-res e-alt-font-lato e-text-chinese-black e-font-14 e-font-weight-600 line-height-20px'>
                                                    {basket_data.strategy_type || "alphaBasket"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-auto ms-lg-auto">
                                    <div className='d-flex justify-content-between gap-4 gap-xl-5 flex-wrap e-bg-magnolia px-4 padding-20px-tb border-radius-8'>
                                        <div>
                                            <p className='e-font-14-res mb-1 e-alt-font-lato e-text-primary-color e-font-16 e-font-weight-600 line-height-18px'>
                                                Objective
                                            </p>
                                            <p className='e-font-14-res mb-0 e-alt-font-lato e-text-chinese-black e-font-16 e-font-weight-600 line-height-20px'>
                                                {basket_data.objective || "--"}
                                            </p>
                                        </div>
                                        {
                                            basket_data.cagr &&
                                            <div>
                                                <p className='e-font-14-res mb-1 e-alt-font-lato e-text-primary-color e-font-16 e-font-weight-600 line-height-18px'>
                                                    {basket_data.cagr.label} CAGR
                                                </p>
                                                <p className={`${basket_data.cagr.value < 0 ? "e-text-red" : "e-text-islamic-green"} e-font-14-res mb-0 e-alt-font-lato  e-font-16 e-font-weight-600 line-height-20px`}>
                                                    {basket_data.cagr.value_str}
                                                </p>
                                            </div>
                                        }
                                        <div>
                                            <p className='e-font-14-res mb-1 e-alt-font-lato e-text-primary-color e-font-16 e-font-weight-600 line-height-18px'>
                                                Min. Investment
                                            </p>
                                            {
                                                basket_data.min_investment &&
                                                <p className='e-font-14-res mb-0 e-alt-font-lato e-text-chinese-black e-font-16 e-font-weight-600 line-height-20px'>
                                                    ₹{basket_data.min_investment.toLocaleString("en-IN")}
                                                </p>
                                            }
                                        </div>
                                        <div>
                                            <p className='e-font-14-res mb-1 e-alt-font-lato e-text-primary-color e-font-16 e-font-weight-600 line-height-18px'>
                                                Risk type
                                            </p>
                                            <Chip label={basket_data.risk_category_info?.risk_category_name}
                                                className={basket_data.risk_category_info?.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                                    basket_data.risk_category_info?.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                                        'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                                        </div>
                                    </div>
                                </div>

                                {/* Tags */}
                                {
                                    basket_data.tag &&
                                    <div className='pt-3'>
                                        <h6 className='mb-2 e-alt-font-lato e-text-white e-font-14 e-font-weight-600 line-height-18px'>
                                            Investment Strategies
                                        </h6>
                                        <Chip label={basket_data.tag}
                                            className="e-text-chinese-black e-border-0-5px-gainsboro e-bg-lavender-light" />
                                    </div>
                                }
                            </div>



                        </div>


                        {
                            showInvestDetail == false ?
                                <div className="row mt-4 row-gap-3">
                                    <div className="order-2 order-md-1 col-12 col-sm-10 col-md-7">
                                        <div className="p-3 card-box-shadow e-bg-white">
                                            <div>
                                                <h6 className='mb-3 e-primary-font-clash-display e-text-chinese-black e-font-16 e-font-weight-500 line-height-24px'>
                                                    {is_sip ? is_sip_due ? "Monthly SIP" : "Monthly SIP (12 Installments)" : "One Time Investment"}
                                                </h6>

                                                {
                                                    is_sip ?
                                                        <>
                                                            {/* SIP amount  */}
                                                            <CustomAmountInput label="SIP amount"
                                                                placeholder="Enter sip amount"
                                                                require
                                                                prefix="₹"
                                                                padding="padding-10px-tb ps-4"
                                                                value={sipAmount}
                                                                error={sipAmountError}
                                                                className="w-100 bg-transparent"
                                                                handleChange={_handleSIPAmountChange} />

                                                            {/* Amount chip */}
                                                            <div className='d-flex gap-1 padding-20px-top pb-3'>
                                                                {SIP_AMOUNT.map((amount, key) => (
                                                                    <p key={key} className={`${sipAmount === amount.amount ? 'e-border-0-5-ocean-blue' : 'e-border-0-5-light-silver'} mb-0 e-alt-font-lato e-text-chinese-black e-font-12 e-font-weight-500 line-height-16px border-radius-24 py-2 padding-12px-lr e-bg-white cursor-pointer`}
                                                                        onClick={() => _handleSelectAmount(1, amount)}>
                                                                        +₹{amount.amount}
                                                                    </p>
                                                                ))}
                                                            </div>

                                                        </>
                                                        :
                                                        <>
                                                            {/* Lumpsum amount  */}
                                                            <CustomAmountInput label="Lumpsum amount"
                                                                placeholder="Enter amount"
                                                                require
                                                                prefix="₹"
                                                                padding="padding-10px-tb ps-4"
                                                                value={lumpSumAmount}
                                                                error={lumpSumAmountError}
                                                                className="w-100 bg-transparent"
                                                                handleChange={_handleLumpSumChange} />

                                                            {/* Amount chip */}
                                                            <div className='d-flex gap-1 padding-20px-top pb-3'>
                                                                {
                                                                    SIP_AMOUNT.map((amount, key) => (
                                                                        <p key={key} className={`${lumpSumAmount === amount.amount ? 'e-border-0-5-ocean-blue' : 'e-border-0-5-light-silver'} mb-0 e-alt-font-lato e-text-chinese-black e-font-12 e-font-weight-500 line-height-16px border-radius-24 py-2 padding-12px-lr e-bg-white cursor-pointer`}
                                                                            onClick={() => _handleSelectAmount(2, amount)}>
                                                                            +₹{amount.amount}
                                                                        </p>
                                                                    ))}
                                                            </div>
                                                        </>
                                                }

                                                <div className="position-relative">
                                                    <p className={`position-absolute start-0 top-0 mb-0 e-text-vermilion e-alt-font-lato e-font-12 line-height-12px pt-1 e-font-weight-500`}>
                                                        {
                                                            amountInfoError.current.add_more && (
                                                                <span>
                                                                    Add ₹{amountInfoError.current.add_more.toLocaleString("en-IN")} more amount to the {is_sip ? "SIP" : "Lumpsum"}
                                                                </span>)
                                                        }
                                                        {
                                                            amountInfoError.current.reduce && amountInfoError.current.add_more &&
                                                            <span className="px-1">
                                                                or
                                                            </span>
                                                        }
                                                        {
                                                            amountInfoError.current.reduce &&
                                                            <span>
                                                                Reduce ₹{amountInfoError.current.reduce.toLocaleString("en-IN")} amount to the {is_sip ? "SIP" : "Lumpsum"}
                                                            </span>
                                                        }
                                                    </p>

                                                    <PrimaryButton name="Continue"
                                                        className="w-100 mt-4 h-48"
                                                        status={loading}
                                                        disabled={loading}
                                                        onPress={_handleGeneratePortfolio} />

                                                </div>


                                            </div>
                                        </div>
                                    </div>
                                    <div className="order-1 order-md-2 col-12 col-sm-8 col-md-5">
                                        <ProfileCard navigate={true}
                                            type={2}
                                            brokerClientCode={broker_data.client_code}
                                            brokerAccountBalance={brokerBalance}
                                            handleUpdateBalance={_handleUpdateBrokerACBalance} />
                                    </div>
                                </div>

                                :
                                <div className="row mt-4 row-gap-3">
                                    <div className="col-auto">
                                        <div className="p-3 card-box-shadow e-bg-white">
                                            <Icon icon="left-arrow"
                                                className="cursor-pointer mb-2"
                                                size="18px"
                                                color="#523FC0"
                                                onClick={_handleBack} />

                                            <h6 className="e-text-black e-primary-font-clash-display e-font-weight-500 e-font-16 line-height-24px">
                                                Basket Constituents
                                            </h6>
                                            <div className="e-border-1px-anti-flash-white border-radius-8">
                                                <div className="d-flex  justify-content-between e-bg-magnolia-light">
                                                    <div className="col-4 col-sm-7 py-2 ps-3">
                                                        <span className="e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                            {holdingsData.length === 1 ? "Stock" : "Stocks"} ({holdingsData.length})
                                                        </span>
                                                    </div>

                                                    <div className="col-4 col-sm-2 text-center py-2">
                                                        <span className="ps-3 e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                            Weightage
                                                        </span>
                                                    </div>
                                                    <div className="offset-sm-1 col-4 col-sm-2 text-end e-bg-magnolia-light py-2 pe-sm-3">
                                                        <span className="pe-3 e-text-black e-alt-font-lato e-font-weight-600 e-font-12 line-height-20px">
                                                            Amount
                                                        </span>
                                                    </div>
                                                </div>
                                                {
                                                    holdingsData && holdingsData.map((item, index) => (
                                                        <React.Fragment key={index}>

                                                            <div className="d-flex gap-2 px-3 py-2 justify-content-between align-items-center">
                                                                <div className="col-4 col-sm-7">
                                                                    <div className="d-flex flex-column">
                                                                        <span className="e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-20px">
                                                                            {item.script_name || item.holding_name}
                                                                        </span>
                                                                        <span className="e-text-slate-gray e-font-weight-400 e-font-12 line-height-18px">
                                                                            {item.exchange_to_trade === "NSE" ? item.nse_exchange_symbol : item.bse_exchange_symbol}
                                                                        </span>
                                                                        <span className="e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-12 line-height-20px">
                                                                            Quantity:
                                                                            <span className="ps-1 e-text-primary-color">
                                                                                {item.qty || item.quantity}
                                                                            </span>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-4 col-sm-2 text-center">
                                                                    <span className="text-nowrap e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                                        {item.weight.toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                                <div className="offset-sm-1 col-4 col-sm-2 text-end pe-3">
                                                                    <span className="e-font-14-res text-nowrap e-text-chinese-black e-alt-font-lato e-font-weight-600 e-font-16 line-height-20px e-letter-spacing-min-point-1px">
                                                                        {_formattedAmount(item.value || item.invested_amount ? item.value || item.invested_amount : 0)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <hr className="mx-3 my-0 e-text-anti-flash-white opacity-100" />
                                                        </React.Fragment>

                                                    ))
                                                }
                                                <div className="d-flex px-3 py-2 mt-2 justify-content-between">
                                                    <div className="col-4 col-sm-7">
                                                        <span className="e-text-chinese-black e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                            Total
                                                        </span>
                                                    </div>
                                                    <div className="col-4 col-sm-2 text-center">
                                                        <span className="e-text-primary-color e-alt-font-lato e-font-weight-600 e-font-14 line-height-20px e-letter-spacing-min-point-1px">
                                                            100.00%
                                                        </span>
                                                    </div>
                                                    <div className="offset-sm-1 col-4 col-sm-2 text-end">
                                                        <span className="e-font-14-res e-text-chinese-black e-alt-font-lato e-font-weight-600 e-font-16 line-height-20px e-letter-spacing-min-point-1px">
                                                            {_formattedAmount(holdingsData.map(item => Number(item.value || item.invested_amount)).reduce((a, b) => a + b, 0))}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Info */}

                                            <div className="mt-4 d-flex gap-2 py-2 padding-12px-lr e-bg-magnolia e-border-0-5px-pale-lavender border-radius-8">
                                                <Icon icon={"note-icon"}
                                                    className="d-flex w-18px" />
                                                <p className="mb-0 e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-18px">
                                                    If required by your broker, please authenticate T-PIN before placing an order
                                                </p>
                                            </div>

                                            <PrimaryButton name="Confirm Order"
                                                status={loading}
                                                disabled={loading}
                                                fontSize="e-font-16"
                                                className="w-100 mt-4 h-48"
                                                onPress={_handleInvest} />
                                        </div>
                                    </div>
                                </div>


                        }

                        {/* Order status modal */}
                        {
                            showOrderStatusModal &&
                            <OrderStatusModal type={orderStatusData.current.type}
                                action={orderStatusData.current.status_code == 400 ? "ORDER_SUCCESS" : ""}
                                statusMessage={orderStatusData.current.status_message}
                                portfolioId={orderStatusData.current.portfolio_id}
                                handleClose={_handleCloseOrderStatusModal}
                                handleNavigatePortfolio={_handleNavigatePortfolio}
                                handleNavigateOrderDetail={_handleNavigateOrderDetail} />
                        }

                    </section>
                    :
                    <Loader wrapperClassName="min-vh-100 margin-200px-top" />
            }

        </>
    )
}

export default Invest;